import React from 'react';
import { Tabs as AntTabs } from 'antd';

import { useAuthDecision } from '../../auth';
import type { AuthProps } from '../../auth';
import type { Styleable } from '../../variants';

export interface TabItem<K extends string = string> {
    key: K;
    label: React.ReactNode;
    disabled?: boolean;
}

export interface TabsProps<K extends string = string> extends AuthProps, Styleable {
    items: TabItem<K>[];
    activeKey: K;
    onChange: (key: K) => void;
}

/**
 * EAA-1817: Portal 6 frame 47-52's app-detail tab bar (`Tổng quan · Vai trò ·
 * Tài khoản · Tổ chức · Kết nối kỹ thuật · Nhật ký`) hand-rolled its own
 * `role="tablist"` of raw `<button>`s (`AppDetailHeader.tsx`) instead of this
 * — the exact "custom component in only portal" gap the kit exists to close.
 *
 * Ant Design already ships a themed `Tabs` (`antdTheme.ts`'s `Tabs` token
 * block: `inkBarColor`/`itemColor`/`horizontalItemGutter` tuned to this same
 * frame's 2px `#007fa3` underline / `#64707d` inactive / 22px gutter), so this
 * wraps it rather than hand-building a second implementation — same
 * pass-through pattern as `Segmented`. The one thing an antd component token
 * cannot express is the frame's active/inactive **font-weight** split (600 vs
 * 500) — no `TabsToken` field carries a weight — so this wrapper computes it
 * per item instead of adding one more global CSS override.
 *
 * Nav-only by design: items carry no `children`, so antd's own content pane
 * renders empty (zero height) — the page keeps its existing "render the
 * active tab's own component below the header" composition instead of moving
 * per-tab content ownership into this component.
 */
export function Tabs<K extends string = string>({
    items,
    activeKey,
    onChange,
    auth,
    authFallback = 'disable',
    className,
    style,
}: TabsProps<K>): React.ReactElement {
    const decision = useAuthDecision(auth, authFallback);

    if (decision === 'hide') return <></>;

    const disabledAll = decision !== 'allow';
    const antItems = items.map((item) => ({
        key: item.key,
        disabled: disabledAll || item.disabled,
        label: <span style={{ fontWeight: item.key === activeKey ? 600 : 500 }}>{item.label}</span>,
    }));

    return (
        <AntTabs
            activeKey={activeKey}
            onChange={(key) => onChange(key as K)}
            items={antItems}
            className={className}
            style={style}
        />
    );
}

export default Tabs;
